/* eslint-disable react/no-children-prop */
import { useEffect, useState } from "react";
import { Roboto_Mono } from "next/font/google";
import ReactMarkdown from "react-markdown";
import { GetStaticPaths, GetStaticProps } from "next";

import styles from "../../../styles/traits/arcehtype/ArcehtypeTraitPage.module.css";

import archetypesJsonData from "../../../assets/traitInfo/archetypes.json";
import MobileSearchPage from "@/components/publicProfiles/Header/MobileSearchPage";
import { usePublicProfileContext } from "@/context/PublicProfileContext";
import ProfileHeader from "@/components/publicProfiles/Header/ProfileHeader";
import BreadCrumbs from "@/components/shared/breadCrumbs/BreadCrumbs";
import ArchetypeTraitsPageLeftBar from "@/components/traits/arcehtype/ArchetypeTraitsPage/ArchetypeTraitsPageLeftBar";
import Histogram from "@/components/traits/detailsTab/histogram/Histogram";
import { ExtendedArchetype } from "@/models/sharedModels";
import OtherArchetypes from "@/components/traits/detailsTab/archetypes/otherArchetypes/OtherArchetypes.";
import OppositeArchetype from "@/components/traits/detailsTab/archetypes/opposite/OppositeArchetype";
import AboutThisTrait from "@/components/traits/detailsTab/aboutThisTrait/AboutThisTrait";
import Dichotomies from "@/components/traits/detailsTab/archetypes/dichotomies/Dichotomies";
import ArchetypeComponentTree from "@/components/traits/detailsTab/archetypes/archetypeComponents/ArchetypeComponentTree";
import Cartesian from "@/components/traits/detailsTab/archetypes/cartesian/Cartesian";
import ValuesChart from "@/components/traits/detailsTab/archetypes/valuesChart/ValuesChart";
import Footers from "@/components/shared/Footers/Footers";
import TraitsSEOHeader from "@/components/traits/shared/TraitsSEOHeader";
import SwipeTabs from "@/components/shared/SwipeTabs/SwipeTabs";
import LargeTraitsHeading from "@/components/traits/shared/LargeTraitsHeading/LargeTraitsHeading";
import { archetypeMapper } from "@/assets/traitInfo/dimensionData";

type ArchetypeTraitsPageProps = {
  archetype: ExtendedArchetype;
  otherArchetypes: ExtendedArchetype[];
  tabs: string[];
  markdownContent: string[];
  slicedMarkdown: string[];
};

const roboto = Roboto_Mono({
  weight: "400",
  subsets: ["latin"],
});

export default function ArchetypeTraitsPage(props: ArchetypeTraitsPageProps) {
  const { mobileSearchOpen } = usePublicProfileContext();
  const [groupSlug, setGroupSlug] = useState<string>("");

  useEffect(() => {
    if (
      props.archetype.archetypeGroupSlug === "love" ||
      props.archetype.archetypeGroupSlug === "love-style"
    ) {
      setGroupSlug("love-style");
    } else if (props.archetype.archetypeGroupSlug === "identity") {
      setGroupSlug("personality-II");
    } else {
      setGroupSlug(props.archetype.archetypeGroupSlug);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.archetype.slug]);

  return (
    <>
      <TraitsSEOHeader
        title={`Dimensional || ${props.archetype.name}`}
        description={props.archetype.shortDescription ?? props.archetype.name}
      />
      {mobileSearchOpen === true && <MobileSearchPage />}
      <ProfileHeader />
      <div className={styles.main}>
        <div className={styles.top}>
          <BreadCrumbs
            list={[
              "Traits",
              "Archetypes",
              archetypeMapper[props.archetype.archetypeGroupSlug],
              props.archetype.name,
            ]}
            urls={[
              "/traits",
              "/traits/archetypes",
              `/archetype-group/${groupSlug}`,
            ]}
          />
          <LargeTraitsHeading title={props.archetype.name} noMobile />
        </div>
        <div className={styles.parent}>
          <ArchetypeTraitsPageLeftBar archetype={props.archetype} />
          <div className={styles.rightBarMain}>
            <SwipeTabs
              content={[
                <div key={1} className={styles.markdownDiv}>
                  <ReactMarkdown children={props.markdownContent[0]} />
                </div>,
                <div key={2}>
                  <AboutThisTrait
                    traitContent={props.archetype.traitContent}
                    archetypeGroupSlug={props.archetype.archetypeGroupSlug}
                  />
                  <Histogram
                    // @ts-ignore
                    data={props.archetype.traitContent.histogramData}
                  />
                  <OtherArchetypes archetypes={props.otherArchetypes} />
                  {props.archetype.traitContent.oppositeDetails?.oppositeTrait
                    .slug && <OppositeArchetype archetype={props.archetype} />}
                  {props.archetype.traitContent.polarityArchetypeDetails && (
                    <Dichotomies data={props.archetype.traitContent} />
                  )}
                  {props.archetype.traitContent.archetypeDetails
                    ?.componentTraits && (
                    <ArchetypeComponentTree archetype={props.archetype} />
                  )}
                  {props.archetype.archetypeGroupSlug === "work" && (
                    <Cartesian {...props.archetype} />
                  )}
                  {props.archetype.traitContent.traitDetails &&
                    props.archetype.archetypeGroupSlug === "career" && (
                      <ValuesChart {...props.archetype.traitContent} />
                    )}
                </div>,
              ]}
              slicedContent={props.slicedMarkdown}
              tabs={props.tabs}
              urls={`/traits/archetypes/${props.archetype.slug}`}
            />
          </div>
        </div>
      </div>
      <Footers />
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const list: string[] = [];
  archetypesJsonData.forEach((arch) => {
    if (arch.isDiscoverable === true) list.push(arch.slug);
  });
  const paths = list.map((arch) => ({
    params: { slug: arch },
  }));

  return { paths, fallback: false };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  let archetype: any;
  let slug: any;
  let otherSlugs: any[] = [];
  let otherArchetypes: any[] = [];
  let tabs: string[] = ["identity", "Details"];
  let markdownContent: string[] = [];
  let slicedMarkdown: string[] = [];

  if (ctx?.params?.slug) {
    slug = ctx.params.slug;
  }

  if (slug) {
    archetypesJsonData.forEach((arch) => {
      if (arch.slug === slug && arch.archetypeGroupSlug !== "values") {
        archetype = arch;
        arch.traitContent.otherDetails.otherTraits.forEach((otherArch) => {
          otherSlugs.push(otherArch.slug);
        });
      }
    });
  }

  if (otherSlugs.length > 0) {
    archetypesJsonData.forEach((arch) => {
      if (otherSlugs.includes(arch.slug)) {
        otherArchetypes.push(arch);
      }
    });
  }

  if (!archetype) {
    return {
      notFound: true,
    };
  } else {
    archetype.traitContent.markdownSections?.forEach(
      (section: any, index: number) => {
        if (!tabs.includes(section.traitSection as string)) {
          tabs.push(section.traitSection as string);
        }
        markdownContent.push(section.markdownString);
        if (index !== 0) {
          slicedMarkdown.push(section.markdownString);
        }
      }
    );
  }

  return {
    props: {
      archetype: archetype,
      otherArchetypes: otherArchetypes,
      tabs: tabs,
      markdownContent: markdownContent,
      slicedMarkdown: slicedMarkdown,
    },
  };
};
